import { ApplicationStateEnumLabels } from "@boundless/bl-api-client/customer/models/ApplicationStateEnumLabels"
import { ApplicationStatusEnumLabels } from "@boundless/bl-api-client/customer/models/ApplicationStatusEnumLabels"
import { CustomerApplicationsUpdateBody } from "@boundless/bl-api-client/customer/services.zod"
import { Link } from "@tanstack/react-router"
import { ArrowUpDownIcon } from "lucide-react"

import { DownloadButton } from "@/components/documents/DownloadButton"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { formatDate } from "@/lib/utils"

import type { CustomerApplications } from "@boundless/bl-api-client/customer/models/CustomerApplications"
import type { Column, Row } from "@tanstack/react-table"
import type { z } from "zod"

type getApplicationsColumnsParams = {
  onToggle: (
    data: z.infer<typeof CustomerApplicationsUpdateBody>,
    uuid: string,
  ) => void
}
export const getApplicationsColumns = ({
  onToggle,
}: getApplicationsColumnsParams) => [
  {
    accessorKey: "lenderName",
    header: ({ column }: { column: Column<CustomerApplications, unknown> }) => {
      return (
        <div className="flex items-center space-x-1">
          <Button
            variant="ghost"
            className="h-5 w-5 min-w-5"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <ArrowUpDownIcon className="h-4 w-4" />
          </Button>
          <p className="text-center">Lender</p>
        </div>
      )
    },
    cell: ({ row }: { row: Row<CustomerApplications> }) => {
      return <div className="font-small">{row.getValue("lenderName")}</div>
    },
    filterFn: (
      row: Row<CustomerApplications>,
      columnId: string,
      filterValue: unknown,
    ) => {
      return row.getValue(columnId) === filterValue
    },
  },
  {
    accessorKey: "lastModifiedAt",
    header: ({ column }: { column: Column<CustomerApplications, unknown> }) => {
      return (
        <div className="flex items-center space-x-1">
          <Button
            variant="ghost"
            className="h-5 w-5 min-w-5"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <ArrowUpDownIcon className="h-4 w-4" />
          </Button>
          <p className="text-center">Last Updated</p>
        </div>
      )
    },
    cell: ({ row }: { row: Row<CustomerApplications> }) => {
      return (
        <div className="font-small">
          {formatDate(row.getValue("lastModifiedAt"))}
        </div>
      )
    },
    filterFn: (
      row: Row<CustomerApplications>,
      columnId: string,
      filterValue: unknown,
    ) => {
      const { from, to } = filterValue as {
        from: Date | null
        to: Date | null
      }
      const raw = row.getValue(columnId)
      const rowDate = raw ? new Date(raw as string) : null
      if (!rowDate) return false
      if (from && rowDate < from) return false
      if (to && rowDate > to) return false
      return true
    },
  },
  {
    accessorKey: "submittedAt",
    header: ({ column }: { column: Column<CustomerApplications, unknown> }) => {
      return (
        <div className="flex items-center space-x-1">
          <Button
            variant="ghost"
            className="h-5 w-5 min-w-5"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <ArrowUpDownIcon className="h-4 w-4" />
          </Button>
          <p className="text-center">Submitted</p>
        </div>
      )
    },
    cell: ({ row }: { row: Row<CustomerApplications> }) => {
      return (
        <div className="font-small">
          {formatDate(row.getValue("submittedAt"))}
        </div>
      )
    },
    filterFn: (
      row: Row<CustomerApplications>,
      columnId: string,
      filterValue: unknown,
    ) => {
      const { from, to } = filterValue as {
        from: Date | null
        to: Date | null
      }
      const raw = row.getValue(columnId)
      const rowDate = raw ? new Date(raw as string) : null
      if (!rowDate) return false
      if (from && rowDate < from) return false
      if (to && rowDate > to) return false
      return true
    },
  },
  {
    accessorKey: "state",
    header: ({ column }: { column: Column<CustomerApplications, unknown> }) => {
      return (
        <div className="flex items-center space-x-1">
          <Button
            variant="ghost"
            className="h-5 w-5 min-w-5"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <ArrowUpDownIcon className="h-4 w-4" />
          </Button>
          <p className="text-center">State</p>
        </div>
      )
    },
    cell: ({ row }: { row: Row<CustomerApplications> }) => {
      return (
        <div className="font-small capitalize">
          {
            ApplicationStateEnumLabels[
              row.getValue("state") as keyof typeof ApplicationStateEnumLabels
            ]
          }
        </div>
      )
    },
    filterFn: (
      row: Row<CustomerApplications>,
      columnId: string,
      filterValue: unknown,
    ) => {
      if (!filterValue) return true
      return row.getValue(columnId) === filterValue
    },
  },
  {
    accessorKey: "status",
    header: ({ column }: { column: Column<CustomerApplications, unknown> }) => {
      return (
        <div className="flex items-center space-x-1">
          <Button
            variant="ghost"
            className="h-5 w-5 min-w-5"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <ArrowUpDownIcon className="h-4 w-4" />
          </Button>
          <p className="text-center">Status</p>
        </div>
      )
    },
    cell: ({ row }: { row: Row<CustomerApplications> }) => {
      const status = row.getValue("status") as
        | keyof typeof ApplicationStatusEnumLabels
        | ""
      if (!status)
        return <div className="font-small text-muted-foreground">—</div>
      return (
        <div className="font-small capitalize">
          {ApplicationStatusEnumLabels[status]}
        </div>
      )
    },
    filterFn: (
      row: Row<CustomerApplications>,
      columnId: string,
      filterValue: unknown,
    ) => {
      if (!filterValue) return true
      return row.getValue(columnId) === filterValue
    },
  },
  {
    accessorKey: "repositoryAccessEnabled",
    header: "Data Room",
    cell: ({ row }: { row: Row<CustomerApplications> }) => {
      return (
        <div className="flex items-start">
          <Switch
            checked={row.getValue("repositoryAccessEnabled")}
            onCheckedChange={() => onToggle(row.original, row.original.uuid)}
            className="data-[state=checked]:bg-primary data-[state=unchecked]:bg-gray-400"
          />
        </div>
      )
    },
  },
  {
    header: "Details",
    cell: ({ row }: { row: Row<CustomerApplications> }) => {
      return (
        <Link
          to={`/applications/$applicationUuid/summary`}
          params={{ applicationUuid: row.original.uuid }}
          className="text-primary underline"
        >
          Details
        </Link>
      )
    },
  },
  {
    accessorKey: "downloadUrl",
    header: "Download",
    cell: ({ row }: { row: Row<CustomerApplications> }) => {
      return (
        <DownloadButton
          filename="boundless-application.zip"
          url={row.getValue("downloadUrl")}
          className="text-primary underline"
        >
          Download
        </DownloadButton>
      )
    },
  },
]
